import { type RefObject, useEffect, useLayoutEffect, useRef } from "react"

import {
  PRISM_FOCUS_MS,
  PRISM_GUIDING_MS,
} from "@/features/chat/components/prism-retrieval/prism-retrieval-constants"

/**
 * How long past its last part a settle may run before its inline styles are
 * dropped anyway, in case `transitionend` never fires (a hidden tab, a panel
 * scrolled away).
 */
const PRISM_SETTLE_MARGIN_MS = 200

/** One part of a settle: `px` of height closing over Focus, `delayMs` after the turn ends. */
export type PrismSettlePart = { px: number; delayMs: number }

export type PrismSettleOptions = {
  /** Starts the settle this long after the turn ends, to follow another region. */
  delayMs?: number
  /** Closes a collapse in two halves one Guiding beat apart (`getPrismSettleParts`). */
  split?: boolean
}

/**
 * How a region closes `px` of reserved height (#3261). Unsplit, it is one
 * Focus transition `delayMs` after the turn ends. Split, a collapse closes in
 * two halves one Guiding beat apart: the region's own height closes the
 * first, and a bottom margin that holds the second closes a beat later. Both
 * run on the brand curve for Focus; their peaks never coincide, so together
 * they move the answer below at a little over half the speed of one
 * transition. A region that grows, or barely moves, is never split.
 */
export function getPrismSettleParts(
  px: number,
  { delayMs = 0, split = false }: PrismSettleOptions = {}
): PrismSettlePart[] {
  if (!split || px <= 1) return [{ px, delayMs }]
  return [
    { px: px / 2, delayMs },
    { px: px / 2, delayMs: delayMs + PRISM_GUIDING_MS },
  ]
}

/** When the last part of a settle has ended, in ms after the turn ends. */
export function getPrismSettleEndMs({
  delayMs = 0,
  split = false,
}: PrismSettleOptions = {}): number {
  return delayMs + (split ? PRISM_GUIDING_MS : 0) + PRISM_FOCUS_MS
}

/**
 * Lets a live Prism region settle into its finished layout on Focus (#3261).
 * While the turn is `running`, the region's height is measured after every
 * commit. When the turn ends, the region renders its final content at once,
 * and this holds it at the live height for that frame, then closes the
 * difference on the brand curve (`.prism-motion .prism-settle`), so the answer
 * below slides instead of jumping. `options` can delay the settle behind
 * another region and split a collapse in two (`getPrismSettleParts`). Nothing
 * happens without `motion`, or for a region that never ran live.
 *
 * Only the element's own inline `height`, `margin-bottom`, `overflow`,
 * `transition-property` (off for the held frame only) and `transition-delay`
 * are written, and all are cleared when the last part of its transition ends.
 */
export function usePrismSettleHeight<T extends HTMLElement>(
  running: boolean,
  motion: boolean,
  { delayMs = 0, split = false }: PrismSettleOptions = {}
): RefObject<T | null> {
  const ref = useRef<T>(null)
  const liveHeight = useRef<number | null>(null)
  const release = useRef<(() => void) | null>(null)

  // The live height after every commit of the running turn.
  useLayoutEffect(() => {
    if (running && motion && ref.current) {
      liveHeight.current = ref.current.getBoundingClientRect().height
    }
  })

  useLayoutEffect(() => {
    const element = ref.current
    const from = liveHeight.current
    if (running || !motion || !element || from === null) return
    liveHeight.current = null
    release.current?.()
    const to = element.getBoundingClientRect().height
    if (Math.abs(to - from) < 0.5) return

    const settleOptions = { delayMs, split }
    const parts = getPrismSettleParts(from - to, settleOptions)
    const first = parts[0]
    const second: PrismSettlePart | undefined = parts.length > 1 ? parts[1] : undefined
    // The part that ends last ends the settle: the margin, when split.
    const lastProperty = second ? "margin-bottom" : "height"
    // Children fade on their own transitions, which bubble up here; only the
    // region's own last part ends the settle.
    const onEnd = (event: TransitionEvent) => {
      if (event.target === element && event.propertyName === lastProperty) release.current?.()
    }
    element.style.overflow = "hidden"
    // Hold the live height for this frame; when split, the bottom margin
    // holds the second half of it. Written with transitions off: the margin
    // is transitionable, so without this it would start to grow from 0
    // instead of holding, and the answer would jump by that half.
    element.style.transitionProperty = "none"
    element.style.height = `${from - (second?.px ?? 0)}px`
    if (second) element.style.marginBottom = `${second.px}px`
    // Commit the held height before the transition starts from it.
    void element.offsetHeight
    element.style.transitionProperty = ""
    element.style.transitionDelay = second
      ? `${first.delayMs}ms, ${second.delayMs}ms`
      : `${first.delayMs}ms`
    element.style.height = `${to}px`
    if (second) element.style.marginBottom = "0px"
    element.addEventListener("transitionend", onEnd)
    const timer = setTimeout(
      () => release.current?.(),
      getPrismSettleEndMs(settleOptions) + PRISM_SETTLE_MARGIN_MS
    )
    release.current = () => {
      clearTimeout(timer)
      element.removeEventListener("transitionend", onEnd)
      element.style.height = ""
      element.style.marginBottom = ""
      element.style.overflow = ""
      element.style.transitionDelay = ""
      release.current = null
    }
  }, [running, motion, delayMs, split])

  useEffect(() => () => release.current?.(), [])

  return ref
}
